import { useMemo } from 'react';
import { LayoutGrid } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { useRoutines, type WebRoutine } from '../../../../lib/routines';
import { type PostConnectedConnectorCardData } from '../../../../lib/post-connected-connector-card';
import { connectorRoutinesForSlugs } from '../../../../lib/routine-groups';
import { ConnectorLogo } from '../../connectors/ConnectorLogo';
import { Card, Typography } from '../../ui';
import { ConnectorRoutineRows } from './ConnectorRoutineRows';
import styles from './PostConnectedConnectorCard.module.css';

/**
 * What a connected app brought the user, rendered UNDER an assistant reply carrying
 * `metadata.postConnectedConnectorCard` — a sibling of `ConnectorConnectCard` and `OpportunityCard`,
 * riding its reply the same way.
 *
 * The sibling it is most easily confused with is the connect card, and they are opposites: that one
 * offers a connection that has not happened and expires, this one describes an account that is
 * already set up. Hence the separate metadata key rather than a mode on one card.
 *
 * <h2>The rows are LIVE, not payload</h2>
 *
 * The message carries the app's identity and the home-widget count; the switch rows come from
 * `useRoutines()`, filtered to this connector. A switch drawn from the turn's snapshot shows the wrong
 * state the moment anything else moves it — the Routines screen, another device, the agent itself — and
 * keeps showing it every time the transcript is scrolled back. Reading live also means a row's words and
 * its state come from the same record the toggle writes, so the two cannot disagree.
 *
 * Two things follow, both deliberate:
 *
 * - **A card in an old message shows today's routines.** It is a control panel that rides a reply, not a
 *   record of that day; the reply's own words are the part that stays a record.
 * - **A routine the owner deleted has no row**, with nothing needing to invalidate the message.
 *
 * <h2>Grouped by what they promise, active first inside each group</h2>
 *
 * The rows themselves are `ConnectorRoutineRows`, shared with the band card's expanded app row, so the
 * grouping, the ordering and the toggle cannot come apart between the two cards.
 */
export function PostConnectedConnectorCard({ card }: { card: PostConnectedConnectorCardData }) {
  const { t } = useTranslation();
  const { state } = useRoutines();

  const widgetCount = card.homeWidgets?.length ?? 0;
  const routines = useMemo(() => connectorRoutines(state.kind === 'ready' ? state.routines : [], card.toolkit), [state, card.toolkit]);
  const seenRef = useItemSeenBi('post_connected_connector_card', {
    additionalFields: { toolkit: card.toolkit, routines: String(routines.length), homeWidgets: String(widgetCount) },
  });

  return (
    <Card
      component="section"
      ref={seenRef}
      className={styles.card}
      aria-label={card.name}
      data-testid="chat-post-connected-connector-card"
      data-toolkit={card.toolkit}
    >
      <div className={styles.head}>
        {/* A synthetic status from the card's own fields, the same bridge ConnectorConnectCard uses:
            the logo component reads a ConnectorStatus, and this card carries the two fields that
            matter. `connected: true` is the honest value here — this card only exists after one. */}
        <ConnectorLogo connector={{ toolkit: card.toolkit, logo: card.logoUrl, connected: true }} name={card.name} size={32} />
        <Typography variant="h3" className={styles.name}>
          {card.name}
        </Typography>
      </div>
      {widgetCount > 0 && (
        <div className={styles.section}>
          <Typography variant="h4" className={styles.sectionLabel}>
            {t('connectors.card.homePageSection')}
          </Typography>
          <div className={styles.widgetLine}>
            <LayoutGrid size={16} aria-hidden className={styles.widgetIcon} />
            <Typography variant="p2">{t(`packs.count.homeCards.${widgetCount === 1 ? 'one' : 'other'}`, { count: widgetCount })}</Typography>
          </div>
        </div>
      )}
      {routines.length > 0 && (
        <div className={styles.section}>
          <Typography variant="h4" className={styles.sectionLabel}>
            {t('routines.title')}
          </Typography>
          <ConnectorRoutineRows routines={routines} />
        </div>
      )}
    </Card>
  );
}

/** This connector's routines, in reading order. The rule is `connectorRoutinesForSlugs` in
 *  `lib/routine-groups` — a lib module, so it is unit-testable without importing this component. */
export function connectorRoutines(routines: WebRoutine[], toolkit: string): WebRoutine[] {
  return connectorRoutinesForSlugs(routines, [toolkit]);
}
